import html2pdf from "html2pdf.js";
import { PDFDocument } from "pdf-lib";

export const injectPrintStyles = (html: string): string => {
  const premiumStyles = `
    <style>      
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        box-sizing: border-box;
      }
      
      body {
        font-family: 'Poppins', sans-serif;
        margin: 0;
      }
      
      /* Prevent overflow causing extra pages */
      html, body {
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      /* Ensure everything fits in A4 width (approx 794px at 96DPI) 
         We force it slightly smaller to be safe against rounding errors */
      .pdf-container {
        width: 100%;
        max-width: 793px !important;
        margin: 0 auto !important;
        padding: 0 !important;
        box-sizing: border-box;
        overflow: hidden !important;
      }

      /* Force each catalog page to perfectly fit A4 aspect ratio (210x297mm) */
      /* This prevents subpixel rounding creep that leads to white gaps and extra blank pages at the end */
      .pdf-container .page, .page, .sheet {
        width: 210mm !important;
        height: 296.7mm !important; 
        max-height: 296.7mm !important;
        min-height: 296.7mm !important;
        overflow: hidden !important;
        box-sizing: border-box !important;
        margin: 0 auto !important;
        padding: 0 !important;
        page-break-inside: avoid !important;
        break-inside: avoid-page !important;
      }

      /* Premium Typography */
      h1, h2, h3, h4, h5, h6 {
        font-weight: 600;
        line-height: 1.2;
        color: #111;
      }

      p {
        color: #4b5563;
        margin-bottom: 0;
      }

      /* Image Handling */
      img {
        max-width: 100%;
        height: auto;
        display: block;
        page-break-inside: avoid;
      }

      /* Page Break Handling */
      .page-break {
        page-break-before: always;
        break-before: page;
        margin: 0 !important;
        height: 0 !important;
      }

      /* Hide trailing page break if it's the very last element */
      .page-break:last-child, 
      .page-break:last-of-type:nth-last-child(1) {
        page-break-before: avoid !important;
        display: none !important;
        height: 0 !important;
      }
      
      .no-break {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }

      /* Product Grid Layout Fixes */
      .grid {
        display: grid;
      }
      
      /* Reset unwanted spacing */
      .sheet {
        margin: 0 !important;
        padding: 0 !important;
        width: 100%;
        min-height: 0;
      }

      /* Special handling for last page to prevent trailing blank */
      .page:last-child,
      .last-page,
      .catalog-container > .page:last-of-type {
        page-break-after: avoid !important;
        break-after: avoid-page !important;
      }
    </style>`;

  const headCloseIdx = html.toLowerCase().indexOf("</head>");
  // Wrap body content in a controlled container if not already
  const bodyRegex = /<body[^>]*>([\s\S]*)<\/body>/i;
  const match = html.match(bodyRegex);
  let wrappedHtml = html;

  if (match && match[1]) {
    // We wrap the inner content of body in our strict container
    const bodyContent = match[1];
    const newBodyContent = `<div class="pdf-container">${bodyContent}</div>`;
    wrappedHtml = html.replace(match[1], newBodyContent);
  } else {
    // Fallback if regex fails, just append to start/end of string (risky, but okay for partials)
    // Better strictly relying on styles if regex misses
  }

  if (headCloseIdx === -1) {
    return premiumStyles + wrappedHtml;
  }
  return wrappedHtml.slice(0, headCloseIdx) + premiumStyles + wrappedHtml.slice(headCloseIdx);
};

export const generatePdfBlob = async (
  html: string,
  options: {
    format?: "a4" | "letter";
    landscape?: boolean;
    includeWatermark?: boolean;
    watermarkText?: string;
  } = {}
): Promise<Blob> => {
  let finalHtml = html;

  finalHtml = injectPrintStyles(finalHtml);

  const opt = {
    margin: [0, 0, 0, 0] as [number, number, number, number],
    filename: 'catalog.pdf',
    image: { type: 'jpeg' as const, quality: 0.95 },
    html2canvas: {
      scale: 1.5,
      useCORS: true,
      logging: false,
      letterRendering: true,
      allowTaint: true,
      scrollY: 0,
      windowWidth: 794,
      width: 794,
      removeContainer: true,
    },
    pagebreak: {
      mode: ['css'],
      avoid: ['.page', '.product-card', '.no-break', 'tr', 'img', '.thankyou-box']
    },
    jsPDF: {
      unit: 'mm',
      format: options.format ?? 'a4',
      orientation: options.landscape ? 'landscape' as const : 'portrait' as const,
    }
  };

  try {
    // Correct Pattern: Create worker, process to PDF, modify PDF, then output
    const worker = html2pdf().set(opt).from(finalHtml).toPdf();

    // Finally generate the blob
    // @ts-ignore - output usually returns a promise that resolves to the result
    const pdfBlob = await worker.output('blob');
    return pdfBlob as Blob;

  } catch (error) {
    console.error("PDF Generation failed:", error);
    throw error;
  }
};
/**
 * Merges multiple PDF Blobs into a single PDF Blob.
 */
export const mergePdfBlobs = async (blobs: Blob[]): Promise<Blob> => {
  const mergedPdf = await PDFDocument.create();

  for (const blob of blobs) {
    const arrayBuffer = await blob.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const copiedPages = await mergedPdf.copyPages(
      pdfDoc,
      pdfDoc.getPageIndices()
    );
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  const finalBytes = await mergedPdf.save();
  return new Blob([finalBytes.buffer as any], { type: "application/pdf" });
};

/**
 * Generates a PDF by merging multiple HTML chunks (Front, Product Chunks, Last).
 * This approach resolves memory/blank page issues for large catalogs.
 */
export const generateChunkedPdfBlob = async (
  chunkedHtml: {
    frontHtml: string;
    productChunks: string[];
    lastHtml: string;
  },
  options: {
    format?: "a4" | "letter";
    landscape?: boolean;
  } = {}
): Promise<Blob> => {
  const { frontHtml, productChunks, lastHtml } = chunkedHtml;
  const pdfBlobs: Blob[] = [];

  try {
    // 1. Generate Front Page PDF
    const frontBlob = await generatePdfBlob(frontHtml, options);
    pdfBlobs.push(frontBlob);

    // 2. Generate Product Chunk PDFs
    for (const chunkHtml of productChunks) {
      const chunkBlob = await generatePdfBlob(chunkHtml, options);
      pdfBlobs.push(chunkBlob);
    }

    // 3. Generate Last Page PDF
    const lastBlob = await generatePdfBlob(lastHtml, options);
    pdfBlobs.push(lastBlob);

    // 4. Merge all PDFs
    return await mergePdfBlobs(pdfBlobs);
  } catch (error) {
    console.error("Chunked PDF Generation failed:", error);
    throw error;
  }
};
